import { SCALE } from '../constants';
import { Box, Typography } from '@material-ui/core';
import React, { useMemo } from 'react';

const HEADER_ROW1_COLOR = '#E0E0E0';
const IN_NUMBER_ROW_COLOR = '#E2D6F0';
const GROUP_COLORS = [
  '#FFF8E7', // 0
  '#E3F2FD', // 1
  '#FCE4EC', // 2
  '#E8F5E9', // 3
  '#E8EAF6', // 4
  '#BBDEFB', // 5
  '#FFE0B2', // 6
];

function metricKeyToLabel(metricKey) {
  const map = {
    bloodPressure: 'Blood Pressure',
    bmi: 'Body Mass Index',
    tc: 'Total Cholesterol',
    ldl: 'LDL',
    hdl: 'HDL',
    triglyceride: 'Triglyceride',
    randomGlucose: 'Blood Glucose',
  };
  return map[metricKey] || metricKey;
}

function formatPct(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  const fixed = n.toFixed(1);
  return `${fixed.endsWith('.0') ? String(Math.round(n)) : fixed}%`;
}

function buildGroups(healthScreenings) {
  if (!healthScreenings || typeof healthScreenings !== 'object') return [];
  // Prefer a stable ordering when possible
  const preferredOrder = [
    'tc',
    'ldl',
    'hdl',
    'triglyceride',
    'randomGlucose',
    'bloodPressure',
    'bmi',
  ];
  const keys = Object.keys(healthScreenings);
  const orderedKeys = [
    ...preferredOrder.filter((k) => keys.includes(k)),
    ...keys.filter((k) => !preferredOrder.includes(k)),
  ];
  return orderedKeys
    .map((metricKey) => {
      const metric = healthScreenings[metricKey];
      const categories = Array.isArray(metric?.categories)
        ? metric.categories
        : [];
      if (!categories.length) return null;
      return {
        metricKey,
        groupLabel: metricKeyToLabel(metricKey),
        metric,
        categories,
      };
    })
    .filter(Boolean);
}

const ConsolidationTable = ({ healthScreenings }) => {
  const groups = useMemo(
    () => buildGroups(healthScreenings),
    [healthScreenings]
  );
  const totalCols = useMemo(
    () => groups.reduce((sum, g) => sum + (g.categories?.length || 0), 0),
    [groups]
  );

  const rowsPct = useMemo(() => {
    const cells = (pick) =>
      groups.flatMap((g) => g.categories.map((c) => pick(c)));
    return [
      { label: 'Female', cells: cells((c) => formatPct(c?.femalePercent)) },
      { label: 'Male', cells: cells((c) => formatPct(c?.malePercent)) },
      { label: 'Subtotal', cells: cells((c) => formatPct(c?.percent)) },
      { label: 'GRAND TOTAL', kind: 'grand', cells: [] },
    ];
  }, [groups]);

  const rowsNum = useMemo(() => {
    const cells = (pick) =>
      groups.flatMap((g) => g.categories.map((c) => pick(c)));
    return [
      { label: 'Female', cells: cells((c) => c?.femaleTotal ?? '') },
      { label: 'Male', cells: cells((c) => c?.maleTotal ?? '') },
      { label: 'Subtotal', cells: cells((c) => c?.subTotal ?? '') },
      { label: 'GRAND TOTAL', kind: 'grand', cells: [] },
    ];
  }, [groups]);

  if (!groups.length || !totalCols) {
    return (
      <Typography variant="body2" color="textSecondary">
        No table data.
      </Typography>
    );
  }

  const tableStyle = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    overflow: 'hidden',
    borderRadius: 12,
  };

  const thBase = {
    border: '1px solid #fff',
    padding: `${6 * SCALE}px ${8 * SCALE}px`,
    fontSize: 8 * SCALE,
    fontFamily: 'Lato',
    lineHeight: `${12 * SCALE}px`,
    textAlign: 'center',
    verticalAlign: 'middle',
    color: '#111',
  };

  const tdBase = {
    border: '1px solid #fff',
    padding: `${4 * SCALE}px ${8 * SCALE}px`,
    fontSize: 8 * SCALE,
    fontFamily: 'OpenSans',
    lineHeight: `${12 * SCALE}px`,
    textAlign: 'center',
    verticalAlign: 'middle',
    background: '#F5F5F5',
    color: '#111',
  };

  const labelCellStyle = {
    ...tdBase,
    textAlign: 'left',
    fontWeight: 600,
    background: '#F5F5F5',
    whiteSpace: 'nowrap',
  };

  const subHeaderStyle = {
    ...thBase,
    fontSize: 8 * SCALE,
    fontWeight: 700,
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
  };

  const renderDataCells = (cells, { bold = false } = {}) => {
    let flatIndex = 0;
    return groups.flatMap((g, groupIdx) =>
      g.categories.map((c, cIdx) => {
        const value = cells[flatIndex++];
        return (
          <td
            key={`${g.metricKey}-${cIdx}-${flatIndex}`}
            style={{
              ...tdBase,
              background: GROUP_COLORS[groupIdx] || '#eee',
              fontWeight: bold ? 700 : 400,
            }}
          >
            {value != null ? String(value) : ''}
          </td>
        );
      })
    );
  };

  const renderGrandTotalCellsPct = () =>
    groups.map((g, groupIdx) => (
      <td
        key={`pct-grand-${g.metricKey}`}
        style={{
          ...tdBase,
          background: GROUP_COLORS[groupIdx] || '#eee',
        }}
        colSpan={g.categories.length}
      >
        100%
      </td>
    ));

  const renderGrandTotalCellsNum = () =>
    groups.map((g, groupIdx) => {
      const metricTotal =
        g?.metric?.total ??
        g?.metric?.totalOfAllTime ??
        g?.metric?.totalThisYear ??
        '';
      const fallbackSum = (g.categories || []).reduce(
        (sum, c) => sum + (Number(c?.total) || 0),
        0
      );
      const value =
        metricTotal !== '' && metricTotal != null ? metricTotal : fallbackSum;
      return (
        <td
          key={`num-grand-${g.metricKey}`}
          style={{
            ...tdBase,
            background: GROUP_COLORS[groupIdx] || '#eee',
          }}
          colSpan={g.categories.length}
        >
          {value != null ? String(value) : ''}
        </td>
      );
    });

  return (
    <Box overflow="auto">
      <table style={tableStyle}>
        <thead>
          <tr>
            <th
              style={{ ...thBase, background: HEADER_ROW1_COLOR }}
              rowSpan={2}
            >
              TestType
            </th>
            {groups.map((g, groupIdx) => (
              <th
                key={g.metricKey}
                style={{
                  ...thBase,
                  background: HEADER_ROW1_COLOR,
                  fontWeight: 700,
                }}
                colSpan={g.categories.length}
              >
                {g.groupLabel}
              </th>
            ))}
          </tr>
          <tr>
            {groups.flatMap((g, groupIdx) =>
              g.categories.map((c, idx) => (
                <th
                  key={`${g.metricKey}-${idx}`}
                  style={{
                    ...subHeaderStyle,
                    background: GROUP_COLORS[groupIdx] || '#eee',
                  }}
                >
                  {String(c?.title ?? '')}
                </th>
              ))
            )}
          </tr>
        </thead>

        <tbody>
          {rowsPct.map((r, rIdx) =>
            r.kind === 'grand' ? (
              <tr key="pct-grand">
                <td style={labelCellStyle}>GRAND TOTAL</td>
                {renderGrandTotalCellsPct()}
              </tr>
            ) : (
              <tr key={`pct-${rIdx}`}>
                <td style={labelCellStyle}>{r.label}</td>
                {renderDataCells(r.cells)}
              </tr>
            )
          )}

          <tr>
            <td
              colSpan={1 + totalCols}
              style={{
                ...tdBase,
                background: IN_NUMBER_ROW_COLOR,
                textAlign: 'left',
                fontWeight: 700,
              }}
            >
              In number
            </td>
          </tr>

          {rowsNum.map((r, rIdx) =>
            r.kind === 'grand' ? (
              <tr key="num-grand">
                <td style={labelCellStyle}>GRAND TOTAL</td>
                {renderGrandTotalCellsNum()}
              </tr>
            ) : (
              <tr key={`num-${rIdx}`}>
                <td style={labelCellStyle}>{r.label}</td>
                {renderDataCells(r.cells)}
              </tr>
            )
          )}
        </tbody>
      </table>
    </Box>
  );
};

export default ConsolidationTable;
